import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import { UserType } from '../../../constants/types';
import { sendOtpRequest, verifyOtpRequest } from '../../../store/duckers/otp/thunk';
import { verifyToken } from '../../../store/duckers/tokens/thunk';
import { StorageKeys } from '../../../constants/localStorage';

/**
 * Sign-in-only variant of the booking auth flow: phone → OTP → done.
 * Persists the verified customer JWT to localStorage on success so the caller
 * can navigate to any customer-authenticated screen (e.g. /existing-slots)
 * without going through the full booking pipeline.
 *
 * Deliberately does NOT: fetch customer details, ask for name, book a slot,
 * or dispatch verifyToken (the caller can rely on downstream screens'
 * background verify effect).
 */
export type SignInStep = 'phone' | 'otp' | 'done' | 'error';

interface UseSignInStateResult {
  currentStep: SignInStep;
  isSubmitting: boolean;
  error?: string;
  cooldownSeconds: number;
  phone: string;
  onPhoneSubmit: (phone: string) => void;
  onOtpSubmit: (code: string) => void;
  onResend: () => void;
  isDone: boolean;
}

const COOLDOWN_SECONDS = 30;

const useSignInState = (): UseSignInStateResult => {
  const dispatch = useDispatch<AppDispatch>();
  const otp = useSelector((s: RootState) => s.otp);

  const [step, setStep] = useState<SignInStep>('phone');
  const [phone, setPhone] = useState<string>('');
  const [cooldownSeconds, setCooldownSeconds] = useState<number>(0);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (cooldownSeconds <= 0) return;
    const t = setTimeout(() => setCooldownSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldownSeconds]);

  useEffect(() => {
    if (step !== 'otp') return;
    if (!otp.verifyOtpRequestSucceeded || !otp.token) return;
    localStorage.setItem(StorageKeys.VerifiedCustomerOtpToken, JSON.stringify(otp.token));
    // Also hydrate state.tokens.userData so downstream screens (specifically
    // /existing-slots) can read the phone via tokenPayload.phoneNumber
    // without waiting for a mount-time verifyToken effect to catch up. Without
    // this, ExistingSlots mounts with isTokenVerified=false and immediately
    // navigates to /not-authorized.
    dispatch(verifyToken(otp.token));
    setStep('done');
  }, [step, otp.verifyOtpRequestSucceeded, otp.token, dispatch]);

  useEffect(() => {
    if (step !== 'otp') return;
    if (otp.error && !otp.verifyingOtpRequest) {
      setError(otp.error);
    }
  }, [step, otp.error, otp.verifyingOtpRequest]);

  const onPhoneSubmit = useCallback(
    (nextPhone: string) => {
      setPhone(nextPhone);
      setError(undefined);
      dispatch(sendOtpRequest({ phoneNumber: nextPhone, userType: UserType.Customer }));
      setCooldownSeconds(COOLDOWN_SECONDS);
      setStep('otp');
    },
    [dispatch],
  );

  const onOtpSubmit = useCallback(
    (code: string) => {
      // Guard against duplicate dispatches — backend consumes the OTP record
      // on first verify; a re-verify returns 400 which surfaces as a generic
      // error to the user.
      if (otp.verifyingOtpRequest || otp.verifyOtpRequestSucceeded) return;
      setError(undefined);
      dispatch(verifyOtpRequest({ phoneNumber: phone, userType: UserType.Customer, otp: code }));
    },
    [dispatch, phone, otp.verifyingOtpRequest, otp.verifyOtpRequestSucceeded],
  );

  const onResend = useCallback(() => {
    if (cooldownSeconds > 0) return;
    dispatch(sendOtpRequest({ phoneNumber: phone, userType: UserType.Customer }));
    setCooldownSeconds(COOLDOWN_SECONDS);
  }, [dispatch, phone, cooldownSeconds]);

  const isSubmitting = useMemo(
    () => Boolean(otp.sendingOtpRequest || otp.verifyingOtpRequest),
    [otp.sendingOtpRequest, otp.verifyingOtpRequest],
  );

  return {
    currentStep: step,
    isSubmitting,
    error,
    cooldownSeconds,
    phone,
    onPhoneSubmit,
    onOtpSubmit,
    onResend,
    isDone: step === 'done',
  };
};

export default useSignInState;
